/* global debounce, trapFocus */

// Styled replacement for window.prompt() used by the cart-drawer quantity
// dropdown's "Other…" option. Returns a Promise that resolves to the parsed
// integer the user entered, or null if they cancelled / entered invalid input.
function promptForQuantity(previous) {
  return new Promise((resolve) => {
    // Defensive: if a prior dialog wasn't cleaned up (e.g. interrupted by
    // a cart re-render), it can stay in the browser's top-layer and silently
    // block clicks everywhere — including the cart drawer's overlay. Close
    // and remove any leftover before opening a fresh one.
    document.querySelectorAll('.qty-prompt-dialog').forEach((stale) => {
      try { if (stale.open) stale.close(); } catch (e) { /* ignore */ }
      stale.remove();
    });

    if (!document.getElementById('qty-prompt-styles')) {
      const style = document.createElement('style');
      style.id = 'qty-prompt-styles';
      style.textContent = `
        .qty-prompt-dialog {
          padding: 0;
          border: 1px solid rgb(var(--text-color) / 0.18);
          border-radius: 8px;
          background: rgb(var(--bg-color));
          color: rgb(var(--text-color));
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
          max-width: 320px;
          width: calc(100vw - 32px);
        }
        .qty-prompt-dialog::backdrop {
          background: rgba(0, 0, 0, 0.4);
        }
        .qty-prompt-form {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .qty-prompt-label {
          font-size: 1.4rem;
          font-weight: 600;
        }
        .qty-prompt-input {
          font: inherit;
          font-size: 1.6rem;
          padding: 10px 12px;
          border: 1px solid rgb(var(--text-color) / 0.25);
          border-radius: 4px;
          background: rgb(var(--bg-color));
          color: rgb(var(--text-color));
          width: 100%;
          box-sizing: border-box;
        }
        .qty-prompt-input:focus {
          outline: 2px solid rgb(var(--text-color) / 0.4);
          outline-offset: 1px;
        }
        .qty-prompt-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
      `;
      document.head.appendChild(style);
    }

    const dlg = document.createElement('dialog');
    dlg.className = 'qty-prompt-dialog';
    dlg.innerHTML = `
      <form method="dialog" class="qty-prompt-form">
        <label class="qty-prompt-label" for="qty-prompt-input">Enter quantity</label>
        <input id="qty-prompt-input" class="qty-prompt-input" type="number" inputmode="numeric" min="1" step="1" value="${previous}" />
        <div class="qty-prompt-actions">
          <button type="button" class="btn btn--secondary" data-action="cancel">Cancel</button>
          <button type="submit" class="btn btn--primary" data-action="ok">OK</button>
        </div>
      </form>
    `;
    document.body.appendChild(dlg);

    const input = dlg.querySelector('#qty-prompt-input');
    const form = dlg.querySelector('form');
    let confirmed = false;

    form.addEventListener('submit', () => { confirmed = true; });
    dlg.querySelector('[data-action="cancel"]').addEventListener('click', () => dlg.close());
    // Click on the backdrop (outside the dialog box) cancels too.
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', () => {
      const parsed = confirmed ? parseInt(input.value, 10) : NaN;
      dlg.remove();
      resolve(Number.isFinite(parsed) && parsed >= 1 ? parsed : null);
    });

    // Fallback for browsers that don't support <dialog> — fall back to prompt().
    if (typeof dlg.showModal !== 'function') {
      dlg.remove();
      const entered = window.prompt('Enter quantity:', previous);
      const parsed = entered === null ? NaN : parseInt(entered, 10);
      resolve(Number.isFinite(parsed) && parsed >= 1 ? parsed : null);
      return;
    }

    try {
      dlg.showModal();
      input.focus();
      input.select();
    } catch (e) {
      // showModal() can throw if the element is already in the DOM in a weird
      // state (e.g. a prior failure). Clean up rather than leak a half-mounted
      // dialog that would block clicks everywhere.
      dlg.remove();
      resolve(null);
    }
  });
}

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      // Sweep up any stale qty-prompt-dialog elements every time cart-items
      // mounts (initial load + every cart re-render). A stuck dialog left in
      // the browser's top-layer silently blocks clicks across the page —
      // including the cart drawer's overlay-click-to-close. This guarantees
      // a cart refresh always restores a clean state.
      document.querySelectorAll('.qty-prompt-dialog').forEach((stale) => {
        try { if (stale.open) stale.close(); } catch (e) { /* ignore */ }
        stale.remove();
      });

      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // −/+ stepper buttons: nudge the sibling number input and fire its change
      // handler (the same debounced path a typed quantity takes).
      const stepBtn = evt.target.closest('[data-qty-step]');
      if (stepBtn) {
        evt.preventDefault();
        const input = stepBtn.parentElement.querySelector('.cart-qty-stepper__input');
        if (!input) return;
        const current = parseInt(input.value, 10) || parseInt(input.dataset.initialValue, 10) || 1;
        const next = Math.max(1, current + parseInt(stepBtn.dataset.qtyStep, 10));
        if (next === current) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      const target = evt.target;
      // Direct numeric entry: ignore empty/invalid values (blur without a number,
      // or 0 typed by hand) by reverting to the last known quantity.
      if (target.matches('.cart-qty-stepper__input')) {
        const parsed = parseInt(target.value, 10);
        if (!(parsed >= 1)) {
          target.value = target.dataset.initialValue || '1';
          return;
        }
      }
      if (target.value === '__other__') {
        // The "Other…" sentinel never represents a real quantity. Ask the
        // customer for one via the in-theme dialog (nicer than the native
        // browser prompt), then call updateQuantity with the parsed number.
        // The cart re-renders after update, so we don't need to insert a new
        // <option> here — the Liquid template will inject the custom value
        // into the rebuilt dropdown via the qty_current_in_list path.
        const previous = target.dataset.initialValue || '1';
        target.value = previous; // visually revert immediately so the dropdown doesn't sit on "Other…"
        promptForQuantity(previous).then((parsed) => {
          if (parsed === null) return;
          this.updateQuantity(target.dataset.index, parsed, document.activeElement.name);
        });
        return;
      }
      this.updateQuantity(target.dataset.index, target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        window.initLazyImages();
        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: data,
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        input.value = input.dataset.initialValue;
        // Stepper inputs live inside a <quantity-input> custom element; the cart
        // drawer's quantity <select> does not, so guard before touching it.
        const qtyEl = input.closest('quantity-input');
        if (qtyEl) qtyEl.currentQty = input.dataset.initialValue;
      } finally {
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${sections}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        window.initLazyImages();

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: document.getElementById('cart-summary').dataset.section,
            selector: '.cart__summary'
          }
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
